import {
  byServicePosition,
  clockAt,
  orderLengths,
  reorder as reorderList,
  runningTimes,
  servicePositionOf,
  teamsForViewer,
  type ChartLayoutPatch,
  type PositionIcon,
} from '@church/domain'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { useAccess, useServiceAccess } from '../access/useAccess.ts'
import { sessionKeys } from '../session/keys.ts'
import { useMyProfile } from '../session/useMyProfile.ts'

/**
 * Services and worship planning: plans, the song library, teams and
 * scheduling. The Music Stand and My Schedule have their own files.
 */

export const servicesKeys = {
  all: () => ['services'] as const,
  types: () => [...servicesKeys.all(), 'types'] as const,
  /** The folders the service types sit in (docs/13-ACCESS.md). */
  folders: () => [...servicesKeys.all(), 'folders'] as const,
  plans: (filters: PlanFilters) => [...servicesKeys.all(), 'plans', filters] as const,
  plan: (id: string) => [...servicesKeys.all(), 'plan', id] as const,
  /** A plan's files (its own, its items', its series') and its printouts: under the plan. */
  planFiles: (id: string) => [...servicesKeys.plan(id), 'files'] as const,
  planDownloads: (id: string) => [...servicesKeys.plan(id), 'downloads'] as const,
  stand: (id: string) => [...servicesKeys.all(), 'stand', id] as const,
  songs: (filters: { q?: string; theme?: string; hidden?: 'exclude' | 'include' | 'only' }) =>
    [...servicesKeys.all(), 'songs', filters] as const,
  song: (id: string) => [...servicesKeys.all(), 'song', id] as const,
  teams: () => [...servicesKeys.all(), 'teams'] as const,
  /** The church's ForServices settings (docs/14: the default level). */
  settings: () => [...servicesKeys.all(), 'settings'] as const,
  /** A person as ForServices shows them (contact details only when returned). */
  person: (id: string) => [...servicesKeys.all(), 'person', id] as const,
  matrix: (teamId: string, from: string, to: string) =>
    [...servicesKeys.all(), 'matrix', teamId, from, to] as const,
  mine: () => [...servicesKeys.all(), 'mine'] as const,
  seriesList: () => [...servicesKeys.all(), 'series'] as const,
  series: (id: string) => [...servicesKeys.all(), 'series', id] as const,
  /**
   * Every change made to one plan, whichever screen made it (mutation keys):
   * its panels each hold their own editor, and the plan's save state reads
   * them all from here.
   */
  planEdits: (planId: string) => [...servicesKeys.all(), 'plan-edits', planId] as const,
  planEdit: (planId: string, edit: PlanEdit) =>
    [...servicesKeys.all(), 'plan-edits', planId, edit] as const,
}

/** The kinds of change a plan editor makes (its mutations, by name). */
export type PlanEdit =
  | 'addItem'
  | 'updateItem'
  | 'updatePlan'
  | 'removeItem'
  | 'reorder'
  | 'goLive'
  | 'addTime'
  | 'updateTime'
  | 'removeTime'

// ---- service types and plans ------------------------------------------------------

export function useServiceTypes(options: { enabled?: boolean } = {}) {
  const api = useApi()
  return useQuery({
    queryKey: servicesKeys.types(),
    enabled: options.enabled,
    queryFn: async () => {
      const { data, error } = await api.api.v1.services['service-types'].get()
      if (error) throw error
      return data
    },
  })
}

export function useCreateServiceType() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      name: string
      defaultStartTime?: string
      campusId?: string | null
      /** The folder it goes in; null or absent at the root. */
      folderId?: string | null
      noteCategories?: string[]
    }) => {
      const { data, error } = await api.api.v1.services['service-types'].post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: servicesKeys.types() })
      // A folder's count of service types.
      void queryClient.invalidateQueries({ queryKey: servicesKeys.folders() })
    },
  })
}

export type ServiceType = NonNullable<ReturnType<typeof useServiceTypes>['data']>[number]

/**
 * One service type: rename it, change its usual start time (prefilled on new
 * plans; existing plans keep theirs) or campus, or archive it — which the
 * server refuses while upcoming plans use it.
 */
export function useServiceTypeEditor() {
  const api = useApi()
  const queryClient = useQueryClient()
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: servicesKeys.types() })
    void queryClient.invalidateQueries({ queryKey: [...servicesKeys.all(), 'plans'] })
  }
  const update = useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string
      name?: string
      defaultStartTime?: string
      campusId?: string | null
      /** The folder it sits in; null at the root. */
      folderId?: string | null
      /** The kinds of note its plans and items carry: Vocals, Lighting… */
      noteCategories?: string[]
    }) => {
      const { data, error } = await api.api.v1.services['service-types']({ id }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: () => {
      refresh()
      void queryClient.invalidateQueries({ queryKey: servicesKeys.folders() })
      // Plans show their type's note categories.
      void queryClient.invalidateQueries({ queryKey: [...servicesKeys.all(), 'plan'] })
    },
  })
  /** The church's own order: every type's id, in the new order. */
  const reorder = useMutation({
    mutationFn: async (ids: string[]) => {
      const { data, error } = await api.api.v1.services['service-types'].order.put({ ids })
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      if (data) queryClient.setQueryData(servicesKeys.types(), data)
    },
  })
  const archive = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.api.v1.services['service-types']({ id }).delete()
      if (error) throw error
    },
    onSuccess: refresh,
  })
  /** One place up (-1) or down (+1) in the church's order. */
  const move = (id: string, by: -1 | 1) => {
    const types = queryClient.getQueryData<ServiceType[]>(servicesKeys.types()) ?? []
    const from = types.findIndex((type) => type.id === id)
    if (from < 0) return
    reorder.mutate(reorderList(types, from, from + by).map((type) => type.id))
  }
  return { update, archive, reorder, move }
}

/** Which plans a list asks for. */
export interface PlanFilters {
  serviceTypeId?: string
  /** From this day on (soonest first: by default from a week ago). */
  from?: string
  /** Up to this day. */
  to?: string
  /** 'desc': latest first — past plans, newest first. */
  order?: 'asc' | 'desc'
  /** A page's size. */
  limit?: number
}

/**
 * Plans by date: one service type's or every one's, a page at a time
 * (`limit`) — soonest first from a day, or latest first (`order: 'desc'`).
 * Each carries its series and its last change (`updatedAt`, `updatedByName`).
 */
export function usePlans(filters: PlanFilters) {
  const api = useApi()
  return useInfiniteQuery({
    queryKey: servicesKeys.plans(filters),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.api.v1.services.plans.get({
        query: {
          ...(filters.serviceTypeId ? { serviceTypeId: filters.serviceTypeId } : {}),
          ...(filters.from ? { from: filters.from } : {}),
          ...(filters.to ? { to: filters.to } : {}),
          ...(filters.order ? { order: filters.order } : {}),
          ...(filters.limit ? { limit: filters.limit } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      })
      if (error) throw error
      return data
    },
    getNextPageParam: (last) => last.nextCursor,
  })
}

export function useCreatePlan() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      serviceTypeId: string
      date: string
      title?: string | null
      startTime?: string
    }) => {
      const { data, error } = await api.api.v1.services.plans.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...servicesKeys.all(), 'plans'] })
      // The service types list counts each type's upcoming plans.
      void queryClient.invalidateQueries({ queryKey: servicesKeys.types() })
    },
  })
}

export function usePlan(planId: string) {
  const api = useApi()
  return useQuery({
    queryKey: servicesKeys.plan(planId),
    enabled: planId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.services.plans({ id: planId }).get()
      if (error) throw error
      return data
    },
  })
}

export type Plan = NonNullable<ReturnType<typeof usePlan>['data']>
export type PlanItem = Plan['items'][number]
export type PlanTime = Plan['times'][number]
export type ServicePosition = 'pre' | 'during' | 'post'

/**
 * The plan editor: add, edit, remove, reorder, go live. A reorder shows the
 * new order — with running times recomputed by the same function the server
 * uses — before the server answers, and rolls back if it refuses.
 */
export function usePlanEditor(planId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const key = servicesKeys.plan(planId)
  const plan = usePlan(planId)
  const put = (data: Plan | undefined) => {
    if (data) queryClient.setQueryData(key, data)
  }

  const addItem = useMutation({
    mutationKey: servicesKeys.planEdit(planId, 'addItem'),
    mutationFn: async (body: {
      kind: 'header' | 'media' | 'item'
      title?: string
      songId?: string | null
      lengthSeconds?: number | null
      servicePosition?: ServicePosition
    }) => {
      const { data, error } = await api.api.v1.services.plans({ id: planId }).items.post(body)
      if (error) throw error
      return data
    },
    onSuccess: put,
  })

  const updateItem = useMutation({
    mutationKey: servicesKeys.planEdit(planId, 'updateItem'),
    mutationFn: async ({
      itemId,
      ...body
    }: {
      itemId: string
      title?: string
      /** Link it to a song (named after it), or null to unlink. */
      songId?: string | null
      /** Who leads it (the song leader): a person; null clears it. */
      leaderPersonId?: string | null
      key?: string | null
      lengthSeconds?: number | null
      notes?: string | null
      /** Another arrangement of the item's song. */
      arrangementId?: string
      /** Its phase: Before, During or After the service. */
      servicePosition?: ServicePosition
      /** This week's order of the song's sections; null plays the arrangement's. */
      sequence?: string[] | null
      /** Notes by category: text sets one, '' clears it. */
      categoryNotes?: Record<string, string | null>
    }) => {
      const { data, error } = await api.api.v1.services
        .plans({ id: planId })
        .items({ itemId })
        .patch(body)
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      put(data)
      // The Stand reads the item's arrangement chart and key; the plan's
      // files and printouts name its items and songs.
      void queryClient.invalidateQueries({ queryKey: servicesKeys.stand(planId) })
      void queryClient.invalidateQueries({ queryKey: servicesKeys.planFiles(planId) })
      void queryClient.invalidateQueries({ queryKey: servicesKeys.planDownloads(planId) })
    },
  })

  /** The plan's own details: title, date, start time, notes, series (null leaves it). */
  const updatePlan = useMutation({
    mutationKey: servicesKeys.planEdit(planId, 'updatePlan'),
    mutationFn: async (body: {
      title?: string | null
      date?: string
      startTime?: string
      notes?: string | null
      categoryNotes?: Record<string, string | null>
      seriesId?: string | null
      serviceTypeId?: string
    }) => {
      const { data, error } = await api.api.v1.services.plans({ id: planId }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      put(data)
      // The plans list and the series pages show the title, date and series.
      void queryClient.invalidateQueries({ queryKey: [...servicesKeys.all(), 'plans'] })
      void queryClient.invalidateQueries({ queryKey: servicesKeys.seriesList() })
      void queryClient.invalidateQueries({ queryKey: servicesKeys.types() })
      // A new date or service type moves it between plans: their arrows change.
      void queryClient.invalidateQueries({
        queryKey: [...servicesKeys.all(), 'plan'],
        predicate: (query) => query.queryKey[2] !== planId,
      })
    },
  })

  const removeItem = useMutation({
    mutationKey: servicesKeys.planEdit(planId, 'removeItem'),
    mutationFn: async (itemId: string) => {
      const { data, error } = await api.api.v1.services
        .plans({ id: planId })
        .items({ itemId })
        .delete()
      if (error) throw error
      return data
    },
    onSuccess: put,
  })

  const reorder = useMutation({
    mutationKey: servicesKeys.planEdit(planId, 'reorder'),
    mutationFn: async (itemIds: string[]) => {
      const { data, error } = await api.api.v1.services.plans({ id: planId }).order.put({ itemIds })
      if (error) throw error
      return data
    },
    onMutate: async (itemIds) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<Plan>(key)
      if (previous) {
        const byId = new Map(previous.items.map((item) => [item.id, item]))
        const ordered = byServicePosition(itemIds.flatMap((id) => byId.get(id) ?? []))
        const times = runningTimes(ordered)
        queryClient.setQueryData<Plan>(key, {
          ...previous,
          items: ordered.map((item, i) => ({
            ...item,
            startsAtSeconds: times[i]?.startsAt ?? 0,
            startsAtClock: clockAt(previous.startTime, times[i]?.startsAt ?? 0),
          })),
        })
      }
      return { previous }
    },
    onError: (_error, _ids, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
    },
    onSuccess: put,
  })

  /**
   * Moves an item within its phase — Before, During or After. A drop past
   * the edge of its phase stops at the edge: its phase changes in its panel,
   * not by dragging.
   */
  const move = (from: number, to: number) => {
    const items = plan.data?.items ?? []
    const where = items[from]?.servicePosition
    const same = items.flatMap((item, i) => (item.servicePosition === where ? [i] : []))
    const first = same[0] ?? 0
    const last = same.at(-1) ?? items.length - 1
    reorder.mutate(
      reorderList(items, from, Math.min(last, Math.max(first, to))).map((item) => item.id),
    )
  }

  /** The plan's times: another service, a rehearsal, a soundcheck. */
  const addTime = useMutation({
    mutationKey: servicesKeys.planEdit(planId, 'addTime'),
    mutationFn: async (body: {
      kind: 'service' | 'rehearsal' | 'other'
      name?: string | null
      date: string
      startTime: string
      endTime?: string | null
    }) => {
      const { data, error } = await api.api.v1.services.plans({ id: planId }).times.post(body)
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      put(data)
      void queryClient.invalidateQueries({ queryKey: [...servicesKeys.all(), 'plans'] })
    },
  })
  const updateTime = useMutation({
    mutationKey: servicesKeys.planEdit(planId, 'updateTime'),
    mutationFn: async ({
      timeId,
      ...body
    }: {
      timeId: string
      kind?: 'service' | 'rehearsal' | 'other'
      name?: string | null
      date?: string
      startTime?: string
      endTime?: string | null
    }) => {
      const { data, error } = await api.api.v1.services
        .plans({ id: planId })
        .times({ timeId })
        .patch(body)
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      put(data)
      void queryClient.invalidateQueries({ queryKey: [...servicesKeys.all(), 'plans'] })
    },
  })
  /** Refused for the last service time: it is the plan's day. */
  const removeTime = useMutation({
    mutationKey: servicesKeys.planEdit(planId, 'removeTime'),
    mutationFn: async (timeId: string) => {
      const { data, error } = await api.api.v1.services
        .plans({ id: planId })
        .times({ timeId })
        .delete()
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      put(data)
      void queryClient.invalidateQueries({ queryKey: [...servicesKeys.all(), 'plans'] })
    },
  })

  const goLive = useMutation({
    mutationKey: servicesKeys.planEdit(planId, 'goLive'),
    mutationFn: async (itemId: string | null) => {
      const { data, error } = await api.api.v1.services.plans({ id: planId }).live.post({ itemId })
      if (error) throw error
      return data
    },
    // The leader sees the move at once (Next on a Sunday can't wait on the
    // network); a refusal puts it back.
    onMutate: async (itemId) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<Plan>(key)
      if (previous) queryClient.setQueryData<Plan>(key, { ...previous, liveItemId: itemId })
      return { previous }
    },
    onError: (_error, _itemId, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
    },
    onSuccess: (data) => {
      const current = queryClient.getQueryData<Plan>(key)
      if (current && data)
        queryClient.setQueryData<Plan>(key, { ...current, liveItemId: data.itemId })
    },
  })

  /** Deletes the plan: its people come off it and its uploaded files are deleted. */
  const deletePlan = useMutation({
    mutationFn: async () => {
      const { error } = await api.api.v1.services.plans({ id: planId }).delete()
      if (error) throw error
    },
    onSuccess: () => {
      // Not refetched (it is gone); marked stale so going back to it asks again.
      void queryClient.invalidateQueries({ queryKey: key, refetchType: 'none' })
      void queryClient.invalidateQueries({ queryKey: [...servicesKeys.all(), 'plans'] })
      void queryClient.invalidateQueries({
        queryKey: [...servicesKeys.all(), 'plan'],
        predicate: (query) => query.queryKey[2] !== planId,
      })
      void queryClient.invalidateQueries({ queryKey: servicesKeys.seriesList() })
      void queryClient.invalidateQueries({ queryKey: servicesKeys.types() })
    },
  })

  return {
    plan: plan.data,
    isLoading: plan.isPending,
    error: plan.error,
    addItem,
    updateItem,
    updatePlan,
    deletePlan,
    removeItem,
    reorder,
    move,
    goLive,
    addTime,
    updateTime,
    removeTime,
  }
}

/**
 * The running order — a plan's or a template's — as one service time reads
 * it: every item in its order (Before items first, After items last, no
 * bands between), each with its clock time counted from that time's start
 * (the 11:00's clock, not the 9:00's), and where it may move (within its
 * phase). No mark: the phase shows in the item's panel. `lengths` is the
 * service's length — During items only — and what runs before and after it.
 * `timeId` null (or not a service time) reads the first service.
 */
export function runningOrderFor<
  T extends { id: string; kind: string; name: string | null; startTime: string },
>(plan: { items: PlanItem[]; startTime: string; times: readonly T[] }, timeId: string | null) {
  // In the order they happen (the API sends them so: a plan's by day, a
  // template's by its day around the plan's).
  const services = plan.times.filter((time) => time.kind === 'service')
  const time = services.find((t) => t.id === timeId) ?? services[0]
  const start = time?.startTime ?? plan.startTime
  const phaseOf = plan.items.map((item) => servicePositionOf(item))
  const rows = plan.items.map((item, index) => {
    const phase = phaseOf[index] ?? 'during'
    return {
      item,
      index,
      clock: clockAt(start, item.startsAtSeconds),
      /** It moves within its phase: up while one of its phase is above it. */
      canUp: phaseOf.slice(0, index).includes(phase),
      canDown: phaseOf.slice(index + 1).includes(phase),
    }
  })
  return { services, time, start, rows, lengths: orderLengths(plan.items) }
}

// ---- scheduling on a plan --------------------------------------------------------------

export function usePlanScheduling(planId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const put = (data: unknown) => {
    if (data) queryClient.setQueryData(servicesKeys.plan(planId), data)
    void queryClient.invalidateQueries({ queryKey: [...servicesKeys.all(), 'matrix'] })
  }
  const assign = useMutation({
    mutationFn: async (body: {
      positionId: string
      personId: string
      override?: boolean
      /** The times they serve; all of them when left out. */
      timeIds?: string[]
    }) => {
      const { data, error } = await api.api.v1.services.plans({ id: planId }).people.post(body)
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  /** Which of the plan's times someone serves (at least one). */
  const setTimes = useMutation({
    mutationFn: async ({ planPersonId, timeIds }: { planPersonId: string; timeIds: string[] }) => {
      const { data, error } = await api.api.v1.services
        .plans({ id: planId })
        .people({ planPersonId })
        .patch({ timeIds })
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  /** How many a position needs on this plan; null goes back to its usual number. */
  const setNeed = useMutation({
    mutationFn: async ({ positionId, needed }: { positionId: string; needed: number | null }) => {
      const { data, error } = await api.api.v1.services
        .plans({ id: planId })
        .needs({ positionId })
        .put({ needed })
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      put(data)
      void queryClient.invalidateQueries({ queryKey: [...servicesKeys.all(), 'plans'] })
    },
  })
  const unassign = useMutation({
    mutationFn: async (planPersonId: string) => {
      const { data, error } = await api.api.v1.services
        .plans({ id: planId })
        .people({ planPersonId })
        .delete()
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  const remind = useMutation({
    mutationFn: async (planPersonId: string) => {
      const { error } = await api.api.v1.services
        .plans({ id: planId })
        .people({ planPersonId })
        .remind.post()
      if (error) throw error
    },
  })
  return { assign, unassign, remind, setTimes, setNeed }
}

// ---- songs --------------------------------------------------------------------------

/** The library; hidden songs only when asked for (`hidden: 'include'` or `'only'`). */
export function useSongs(filters: {
  q?: string
  theme?: string
  hidden?: 'exclude' | 'include' | 'only'
}) {
  const api = useApi()
  // The library is Songs Viewer and up (13-ACCESS): nobody else asks for it.
  const library = useAccess().hasSomewhere('songs', 'viewer')
  return useInfiniteQuery({
    queryKey: servicesKeys.songs(filters),
    enabled: library,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.api.v1.services.songs.get({
        query: {
          ...(filters.q ? { q: filters.q } : {}),
          ...(filters.theme ? { theme: filters.theme } : {}),
          ...(filters.hidden && filters.hidden !== 'exclude' ? { hidden: filters.hidden } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      })
      if (error) throw error
      return data
    },
    getNextPageParam: (last) => last.nextCursor,
  })
}

export function useSong(songId: string) {
  const api = useApi()
  return useQuery({
    queryKey: servicesKeys.song(songId),
    enabled: songId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.services.songs({ id: songId }).get()
      if (error) throw error
      return data
    },
  })
}

export type Song = NonNullable<ReturnType<typeof useSong>['data']>

export function useCreateSong() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      title: string
      author?: string | null
      artist?: string | null
      ccliNumber?: string | null
      /** The first arrangement's original key (a song has none of its own). */
      key?: string | null
      tempo?: number | null
      themes?: string[]
      chordChart?: string | null
      copyright?: string | null
      /** The first arrangement's length. */
      lengthSeconds?: number | null
      /** The Apple Music recording chosen for it: linked as a person's choice. */
      catalogId?: string | null
    }) => {
      const { data, error } = await api.api.v1.services.songs.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: [...servicesKeys.all(), 'songs'] }),
  })
}

export function useSongEditor(songId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const put = (data: unknown) => {
    if (data) queryClient.setQueryData(servicesKeys.song(songId), data)
    void queryClient.invalidateQueries({ queryKey: [...servicesKeys.all(), 'songs'] })
    // An arrangement's printouts follow its keys and chart.
    void queryClient.invalidateQueries({ queryKey: [...servicesKeys.all(), 'arrangements'] })
  }
  const update = useMutation({
    mutationFn: async (body: {
      title?: string
      author?: string | null
      artist?: string | null
      ccliNumber?: string | null
      tempo?: number | null
      themes?: string[]
      copyright?: string | null
      notes?: string | null
      /** Out of the library and pickers; its history stays. */
      hidden?: boolean
    }) => {
      const { data, error } = await api.api.v1.services.songs({ id: songId }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  const addArrangement = useMutation({
    mutationFn: async (body: {
      name: string
      key?: string | null
      bpm?: number | null
      meter?: string | null
      lengthSeconds?: number | null
      chordChart?: string | null
      notes?: string | null
      sequence?: string[]
    }) => {
      const { data, error } = await api.api.v1.services
        .songs({ id: songId })
        .arrangements.post(body)
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  const updateArrangement = useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string
      name?: string
      key?: string | null
      bpm?: number | null
      meter?: string | null
      lengthSeconds?: number | null
      chordChart?: string | null
      notes?: string | null
      sequence?: string[]
      chartLayout?: ChartLayoutPatch
    }) => {
      const { data, error } = await api.api.v1.services.arrangements({ id }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  const addAttachment = useMutation({
    mutationFn: async ({
      arrangementId,
      ...body
    }: {
      arrangementId: string
      kind: 'chart' | 'sheet_music' | 'audio' | 'stems' | 'video' | 'rehearsal' | 'other'
      title: string
      url: string
    }) => {
      const { data, error } = await api.api.v1.services
        .arrangements({ id: arrangementId })
        .attachments.post(body)
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  /** Deletes the song; refused while it is on an upcoming plan. */
  const remove = useMutation({
    mutationFn: async () => {
      const { error } = await api.api.v1.services.songs({ id: songId }).delete()
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: servicesKeys.song(songId) })
      void queryClient.invalidateQueries({ queryKey: [...servicesKeys.all(), 'songs'] })
    },
  })
  /** Archives an arrangement; refused for the last one, or one an upcoming plan plays. */
  const removeArrangement = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await api.api.v1.services.arrangements({ id }).delete()
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  /** A key the arrangement is sung in, tagged for who sings it. */
  const addKey = useMutation({
    mutationFn: async ({
      arrangementId,
      ...body
    }: {
      arrangementId: string
      key: string
      tags?: string[]
    }) => {
      const { data, error } = await api.api.v1.services
        .arrangements({ id: arrangementId })
        .keys.post(body)
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  const updateKey = useMutation({
    mutationFn: async ({ id, ...body }: { id: string; key?: string; tags?: string[] }) => {
      const { data, error } = await api.api.v1.services['arrangement-keys']({ id }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  /**
   * A key's tags. The original key has no row until it is first tagged (its
   * id is null): tagging it adds one.
   */
  const tagKey = useMutation({
    mutationFn: async ({
      arrangementId,
      id,
      key,
      tags,
    }: {
      arrangementId: string
      id: string | null
      key: string
      tags: string[]
    }) => {
      const { data, error } = id
        ? await api.api.v1.services['arrangement-keys']({ id }).patch({ tags })
        : await api.api.v1.services.arrangements({ id: arrangementId }).keys.post({ key, tags })
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  const removeKey = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await api.api.v1.services['arrangement-keys']({ id }).delete()
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  return {
    update,
    addArrangement,
    updateArrangement,
    removeArrangement,
    addKey,
    updateKey,
    tagKey,
    removeKey,
    addAttachment,
    remove,
  }
}

// ---- teams and the schedule ------------------------------------------------------------

/**
 * A person as ForServices shows them (docs/13-ACCESS.md): name, photo,
 * birthday, campus, status — and phones, emails and address only when the
 * server returns them (`contact` is null otherwise).
 */
export function useServicesPerson(personId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: servicesKeys.person(personId ?? ''),
    enabled: personId !== null,
    queryFn: async () => {
      const { data, error } = await api.api.v1.services.people({ id: personId ?? '' }).get()
      if (error) throw error
      return data
    },
  })
}

export type ServicesPerson = NonNullable<ReturnType<typeof useServicesPerson>['data']>

export function useTeams() {
  const api = useApi()
  return useQuery({
    queryKey: servicesKeys.teams(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.services.teams.get()
      if (error) throw error
      return data
    },
  })
}

export type Team = NonNullable<ReturnType<typeof useTeams>['data']>[number]

/**
 * Who serves on a plan, team by team, in the order the signed-in person reads
 * it: their own teams first (`teamsForViewer`), then the rest alphabetically.
 * Each position carries its people and how full it is. A team of other
 * service types only (docs/14: a team belongs to one or more) is left out
 * unless someone on this plan serves in it.
 */
export function usePlanTeams(planId: string) {
  const plan = usePlan(planId).data
  const { data: teams } = useTeams()
  const { data: me } = useMyProfile()
  const access = useServiceAccess()
  if (!plan || !teams) return null
  const people = plan.people
  const relevant = teams.filter(
    (team) =>
      team.serviceTypeIds.includes(plan.serviceTypeId) || people.some((p) => p.teamId === team.id),
  )
  const personId = me?.id ?? null
  const serving = people.filter((p) => p.personId === personId).map((p) => p.teamId)
  const groups = teamsForViewer(relevant, personId, serving).map(({ team, mine }) => ({
    team,
    mine,
    /**
     * They ask people for it and change their places: Scheduler on the
     * plan's service type, or leading this team (docs/14).
     */
    schedules: access.canScheduleTeam(team, plan.serviceTypeId),
    positions: team.positions.map((position) => {
      const here = people.filter((p) => p.positionId === position.id)
      const filled = here.filter((p) => p.status !== 'declined').length
      // This plan's own count for the position, when it has one.
      const override = plan.needs.find((n) => n.positionId === position.id)?.needed
      const needed = override ?? position.neededPerPlan
      return {
        ...position,
        teamName: team.name,
        people: here,
        filled,
        needed,
        /** True when this plan needs a different number than the position's usual. */
        overridden: override !== undefined,
        /** Places nobody has been asked for yet. */
        open: Math.max(0, needed - filled),
        full: filled >= needed,
      }
    }),
  }))
  // A team at a glance: who said yes, who has not answered, who said no,
  // what is still open.
  const counted = groups.map((group) => {
    const all = group.positions.flatMap((p) => p.people)
    return {
      ...group,
      counts: {
        confirmed: all.filter((p) => p.status === 'accepted').length,
        pending: all.filter((p) => p.status === 'requested').length,
        declined: all.filter((p) => p.status === 'declined').length,
        needed: group.positions.reduce((sum, p) => sum + p.open, 0),
      },
    }
  })
  const positions = counted.flatMap((g) => g.positions)
  // The teams they schedule here, and those teams' positions (what Ask offers).
  const schedulable = new Set(counted.filter((g) => g.schedules).map((g) => g.team.id))
  return {
    groups: counted,
    positions,
    schedulablePositions: counted.filter((g) => g.schedules).flatMap((g) => g.positions),
    /** They schedule someone on this plan's team. */
    schedulesTeam: (teamId: string) => schedulable.has(teamId),
    schedulesAny: schedulable.size > 0,
  }
}

export type PlanTeams = NonNullable<ReturnType<typeof usePlanTeams>>

export function useTeamEditor() {
  const api = useApi()
  const queryClient = useQueryClient()
  const put = (data: unknown) => {
    if (data) queryClient.setQueryData(servicesKeys.teams(), data)
  }
  /**
   * Where a team serves or who leads it changed: plans count its positions
   * on other service types now, and a leader's controls follow `/me`.
   */
  const reach = (data: unknown) => {
    put(data)
    void queryClient.invalidateQueries({ queryKey: [...servicesKeys.all(), 'plans'] })
    void queryClient.invalidateQueries({ queryKey: sessionKeys.me() })
  }
  const createTeam = useMutation({
    mutationFn: async (body: {
      name: string
      /** At least one: every team serves a service type (docs/14). */
      serviceTypeIds: string[]
      defaultMaxPerMonth?: number | null
    }) => {
      const { data, error } = await api.api.v1.services.teams.post(body)
      if (error) throw error
      return data
    },
    onSuccess: reach,
  })
  const addPosition = useMutation({
    mutationFn: async ({
      teamId,
      ...body
    }: {
      teamId: string
      name: string
      neededPerPlan?: number
      icon?: PositionIcon | null
    }) => {
      const { data, error } = await api.api.v1.services.teams({ id: teamId }).positions.post(body)
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  const setMembers = useMutation({
    mutationFn: async ({ positionId, personIds }: { positionId: string; personIds: string[] }) => {
      const { data, error } = await api.api.v1.services
        .positions({ id: positionId })
        .members.put({ personIds })
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  const updateTeam = useMutation({
    mutationFn: async ({
      teamId,
      ...body
    }: {
      teamId: string
      name?: string
      defaultMaxPerMonth?: number | null
      /** Its service types as a whole: at least one (the server refuses none). */
      serviceTypeIds?: string[]
    }) => {
      const { data, error } = await api.api.v1.services.teams({ id: teamId }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: reach,
  })
  const updatePosition = useMutation({
    mutationFn: async ({
      positionId,
      ...body
    }: {
      positionId: string
      name?: string
      neededPerPlan?: number
      icon?: PositionIcon | null
    }) => {
      const { data, error } = await api.api.v1.services.positions({ id: positionId }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  /** Archives a position; refused while anyone is asked for it on an upcoming plan. */
  const archivePosition = useMutation({
    mutationFn: async (positionId: string) => {
      const { data, error } = await api.api.v1.services.positions({ id: positionId }).delete()
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  /** Another service type for a team ("Add existing team" in a service type's settings). */
  const addServiceType = useMutation({
    mutationFn: async ({ teamId, serviceTypeId }: { teamId: string; serviceTypeId: string }) => {
      const { data, error } = await api.api.v1.services
        .teams({ id: teamId })
        ['service-types'].post({ serviceTypeId })
      if (error) throw error
      return data
    },
    onSuccess: reach,
  })
  /** A team off one service type only (never its last one: 422, delete it instead). */
  const removeServiceType = useMutation({
    mutationFn: async ({ teamId, serviceTypeId }: { teamId: string; serviceTypeId: string }) => {
      const { data, error } = await api.api.v1.services
        .teams({ id: teamId })
        ['service-types']({ serviceTypeId })
        .delete()
      if (error) throw error
      return data
    },
    onSuccess: reach,
  })
  /** Deletes a team from every service type; refused while anyone is asked for it on an upcoming plan. */
  const deleteTeam = useMutation({
    mutationFn: async (teamId: string) => {
      const { data, error } = await api.api.v1.services.teams({ id: teamId }).delete()
      if (error) throw error
      return data
    },
    onSuccess: reach,
  })
  /** The church's own order for its teams. */
  const reorderTeams = useMutation({
    mutationFn: async (ids: string[]) => {
      const { data, error } = await api.api.v1.services.teams.order.put({ ids })
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  /** A team's positions in order. */
  const reorderPositions = useMutation({
    mutationFn: async ({ teamId, ids }: { teamId: string; ids: string[] }) => {
      const { data, error } = await api.api.v1.services
        .teams({ id: teamId })
        .positions.order.put({ ids })
      if (error) throw error
      return data
    },
    onSuccess: put,
  })
  /** Who leads a team. */
  const setLeaders = useMutation({
    mutationFn: async ({ teamId, personIds }: { teamId: string; personIds: string[] }) => {
      const { data, error } = await api.api.v1.services
        .teams({ id: teamId })
        .leaders.put({ personIds })
      if (error) throw error
      return data
    },
    onSuccess: reach,
  })
  const cached = () => queryClient.getQueryData<Team[]>(servicesKeys.teams()) ?? []
  /** A team one place up (-1) or down (+1) in the church's order. */
  const moveTeam = (teamId: string, by: -1 | 1) => {
    const teams = cached()
    const from = teams.findIndex((team) => team.id === teamId)
    if (from < 0) return
    reorderTeams.mutate(reorderList(teams, from, from + by).map((team) => team.id))
  }
  /** A position one place up or down within its team. */
  const movePosition = (teamId: string, positionId: string, by: -1 | 1) => {
    const positions = cached().find((team) => team.id === teamId)?.positions ?? []
    const from = positions.findIndex((p) => p.id === positionId)
    if (from < 0) return
    reorderPositions.mutate({
      teamId,
      ids: reorderList(positions, from, from + by).map((p) => p.id),
    })
  }
  return {
    createTeam,
    addPosition,
    setMembers,
    updateTeam,
    updatePosition,
    archivePosition,
    addServiceType,
    removeServiceType,
    deleteTeam,
    reorderTeams,
    reorderPositions,
    setLeaders,
    moveTeam,
    movePosition,
  }
}

export function useScheduleMatrix(teamId: string, from: string, to: string) {
  const api = useApi()
  return useQuery({
    queryKey: servicesKeys.matrix(teamId, from, to),
    enabled: teamId !== '',
    queryFn: async () => {
      const { data, error } = await api.api.v1.services.schedule.matrix.get({
        query: { teamId, from, to },
      })
      if (error) throw error
      return data
    },
  })
}

export function useAutoSchedule() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { teamId: string; from: string; to: string; apply: boolean }) => {
      const { data, error } = await api.api.v1.services.schedule.auto.post(body)
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      if (data?.applied) void queryClient.invalidateQueries({ queryKey: servicesKeys.all() })
    },
  })
}

// ---- series ------------------------------------------------------------------------

/** Where a series' art is served from: proxied, so the session applies. */
export const seriesArtUrl = (seriesId: string, version?: string | null) =>
  `/api/v1/services/series/${seriesId}/art${version ? `?v=${encodeURIComponent(version)}` : ''}`

export const SERIES_ART_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const
type SeriesArtType = (typeof SERIES_ART_TYPES)[number]

export function useSeriesList() {
  const api = useApi()
  return useQuery({
    queryKey: servicesKeys.seriesList(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.services.series.get()
      if (error) throw error
      return data
    },
  })
}

export function useSeries(seriesId: string | null) {
  const api = useApi()
  return useQuery({
    queryKey: servicesKeys.series(seriesId ?? ''),
    enabled: Boolean(seriesId),
    queryFn: async () => {
      const { data, error } = await api.api.v1.services.series({ id: seriesId ?? '' }).get()
      if (error) throw error
      return data
    },
  })
}

export type Series = NonNullable<ReturnType<typeof useSeries>['data']>
export type SeriesSummary = NonNullable<ReturnType<typeof useSeriesList>['data']>[number]

export function useCreateSeries() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      name: string
      description?: string | null
      startsOn?: string | null
      endsOn?: string | null
    }) => {
      const { data, error } = await api.api.v1.services.series.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: servicesKeys.seriesList() }),
  })
}

/**
 * A series: its details, its art (three steps — a presigned URL, the bytes
 * straight to storage, then confirm), and deleting it. Every change refreshes
 * the plans that show it.
 */
export function useSeriesEditor(seriesId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: servicesKeys.seriesList() })
    void queryClient.invalidateQueries({ queryKey: servicesKeys.series(seriesId) })
    void queryClient.invalidateQueries({ queryKey: [...servicesKeys.all(), 'plans'] })
    void queryClient.invalidateQueries({ queryKey: [...servicesKeys.all(), 'plan'] })
  }

  const update = useMutation({
    mutationFn: async (body: {
      name?: string
      description?: string | null
      startsOn?: string | null
      endsOn?: string | null
    }) => {
      const { data, error } = await api.api.v1.services.series({ id: seriesId }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: refresh,
  })

  const uploadArt = useMutation({
    mutationFn: async (file: { type: string; body: Blob | ArrayBuffer }) => {
      if (!SERIES_ART_TYPES.includes(file.type as SeriesArtType))
        throw new Error('Series art must be JPEG, PNG or WebP.')
      const slot = await api.api.v1.services.series({ id: seriesId }).art['upload-url'].post({
        contentType: file.type as SeriesArtType,
        sizeBytes: file.body instanceof Blob ? file.body.size : file.body.byteLength,
      })
      if (slot.error) throw slot.error
      const uploaded = await fetch(slot.data.url, {
        method: 'PUT',
        headers: { 'content-type': file.type },
        body: file.body,
      })
      if (!uploaded.ok) throw new Error(`Storage rejected the upload (${uploaded.status}).`)
      const { data, error } = await api.api.v1.services
        .series({ id: seriesId })
        .art.put({ key: slot.data.key })
      if (error) throw error
      return data
    },
    onSuccess: refresh,
  })

  const removeArt = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.api.v1.services.series({ id: seriesId }).art.delete()
      if (error) throw error
      return data
    },
    onSuccess: refresh,
  })

  const remove = useMutation({
    mutationFn: async () => {
      const { error } = await api.api.v1.services.series({ id: seriesId }).delete()
      if (error) throw error
    },
    onSuccess: refresh,
  })

  return { update, uploadArt, removeArt, remove }
}
